import { Injectable } from '@angular/core';
import * as _ from 'lodash';

function rolesToApi(role) {
    return {
        'role_id': _.get(role, 'id', 0),
        permission: _.get(role, 'roleSettings[0].value', 0)
    };
}

class Mappings {
    rolePermissionToApi = (rp) => {
        return {
            'role_id': _.get(rp, 'role.id', 0),
            'permission': _.get(rp, 'permission', -2)
        };
    }

    userPermissionToApi = (up) => {
        return {
            'user_id': _.get(up, 'user.id', 0),
            'permission': _.get(up, 'permission', -2)
        };
    }

    aclToApi = (aclEntry) => {
        return {
            'module_id': _.get(aclEntry, 'id', 0),
            'path': _.get(aclEntry, 'path', ''),
            'rolePermissions': _.map(
                _.get(aclEntry, 'rolePermissions', []),
                this.rolePermissionToApi
            ),
            'userPermissions': _.map(
                _.get(aclEntry, 'userPermissions', []),
                this.userPermissionToApi
            )
        };
    }

    rolesToApi(role) {
        return rolesToApi(role);
    }
}

class Path {
    getPath = (aclList, path) => {
        return _.find(aclList, { 'path': path });
    };

    pathExists = (aclList, path) => {
        return !_.isEmpty(this.getPath(aclList, path));
    };

    createPath = (newPath, roles) => {
        return {
            'id': 0,
            'path': newPath,
            'userPermissions': [],
            'rolePermissions': _.map(roles, rolesToApi)
        };
    };

    removePath = (aclList, path) => {
        return _.without(aclList, this.getPath(aclList, path));
    };
}

@Injectable()
export class ScmAclService {
    public mapping: Mappings;
    public path: Path;

    constructor() {
        this.mapping = new Mappings();
        this.path = new Path();
    }

    notAdminRole(role) {
        return _.get(role, 'roleName', '') !== 'Admin';
    }
    adminRole(role) {
        return _.get(role, 'roleName', '') === 'Admin';
    }
    configurableRole = (role) => {
        return !(this.adminRole(role) || this.notAllowedRole(role));
    }
    notAllowedRole(role) {
        return _.get(role, 'roleSettings[0].value', -1) < 0;
    }
    getNonAdminRoles(roles) {
        return _.filter(roles, this.notAdminRole);
    }
    getAdminRoles(roles) {
        return _.filter(roles, this.adminRole);
    }
    getConfigurableRoles(roles) {
        return _.filter(roles, this.configurableRole);
    }
    getNonAllowedRoles(roles) {
        return _.filter(roles, this.notAllowedRole);
    }

    hasUserAcls(aclList) {
        const count = _.reduce(
            aclList,
            (carry, item) => carry + _.get(item, 'userPermissions.length', 0),
            0
        );
        return count > 0;
    }

    getRolePermission(acl, roleId) {
        return _.find(
            _.get(acl, 'rolePermissions', []),
            (perm) => _.get(perm, 'role.id', 0) == roleId
        );
    }

    getRolePermissionValue(roles, acl, roleId) {
        const def = _.get(
            _.find(roles, (item) => item.id == roleId),
            'roleSettings[0].value',
            -1
        );
        return _.get(this.getRolePermission(acl, roleId), 'permission', def);
    }

    getRolePermissionLabel(permission, options, type) {
        return _.get(
            _.find(_.get(options, type, []), { 'id': permission }),
            'label',
            type === 'git' ? 'Read-Only' : 'No Access'
        );
    }

    getRolePermissionCSSClass(type, value) {
        if (type === 'git') {
            return value === 1 ? 'btn-success' : 'btn-default';
        } else {
            if (value === 1) return 'btn-success';
            if (value < 0) return 'btn-danger';
            return 'btn-warning';
        }
    }

    createRolePermission(roleId, permission) {
        return { 'role': { 'id': roleId }, 'permission': permission };
    }

    addRolePermission(acl, roleId, permission) {
        return _.set(
            acl,
            'rolePermissions',
            _.concat(_.get(acl, 'rolePermissions', []), [this.createRolePermission(roleId, permission)])
        );
    }

    removeRolePermission(acl, role) {
        return _.set(
            acl,
            'rolePermissions',
            _.without(_.get(acl, 'rolePermissions', []), role)
        );
    }

    removeUserPermission(acl, user) {
        return _.set(
            acl,
            'userPermissions',
            _.without(_.get(acl, 'userPermissions', []), user)
        );
    }

    setRolePermission(acl, roleId, permission) {
        const rp = this.getRolePermission(acl, roleId);
        if (!rp && permission !== -2) {
            this.addRolePermission(acl, roleId, permission);
        } else {
            if (permission === -2) {
                this.removeRolePermission(acl, rp);
            } else {
                _.set(rp, 'permission', permission);
            }
        }
        return acl;
    }

    getScmAccessOptions() {
        return {
            'git': [
                { 'id': 0, 'label': 'Read-Only' },
                { 'id': 1, 'label': 'Read/Write' }
            ],
            'svn': [
                { 'id': -1, 'label': 'No Access' },
                { 'id': 0, 'label': 'Read-Only' },
                { 'id': 1, 'label': 'Read/Write' }
            ]
        };
    }
}
